import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

// Public Website
import LandingPage from './pages/LandingPage';
import UniversitiesListPage from './pages/UniversitiesListPage';
import CoursesListPage from './pages/CoursesListPage';
import CountriesListPage from './pages/CountriesListPage';
import ScholarshipsPage from './pages/ScholarshipsPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import FAQPage from './pages/FAQPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';

// Admin (auth required)
import DashboardPage from './pages/DashboardPage';
import ApplicationsPage from './pages/ApplicationsPage';
import ApplicationDetailPage from './pages/ApplicationDetailPage';
import LeadsPage from './pages/LeadsPage';
import FormCreatorPage from './pages/FormCreatorPage';
import WebFormsPage from './pages/WebFormsPage';
import CoreSettingsPage from './pages/CoreSettingsPage';
import OrganizationSettingsPage from './pages/OrganizationSettingsPage';
import OfficesPage from './pages/OfficesPage';
import TeamPage from './pages/TeamPage';
import AgentsPage from './pages/AgentsPage';
import StudentsPage from './pages/StudentsPage';
import AgentsManagerPage from './pages/AgentsManagerPage';
import PendingAgentsPage from './pages/PendingAgentsPage';
import AICreditsPage from './pages/AICreditsPage';
import NotificationsPage from './pages/NotificationsPage';
import SMTPSettingsPage from './pages/SMTPSettingsPage';
import TwilioSettingsPage from './pages/TwilioSettingsPage';
import BillingCreditsPage from './pages/BillingCreditsPage';
import ReportBuilderPage from './pages/ReportBuilderPage';
import IntegrationsPage from './pages/IntegrationsPage';
import ProductManualPage from './pages/ProductManualPage';
import SupportPage from './pages/SupportPage';
import ArchivedPage from './pages/ArchivedPage';
import ImportCoursesPage from './pages/ImportCoursesPage';
import CourseDetailPage from './pages/CourseDetailPage';

// Student "My Account" (public-website-scoped)
import MyAccountPage from './pages/MyAccountPage';
import MyProfilePage from './pages/MyProfilePage';
import MyApplicationsPage from './pages/MyApplicationsPage';
import MyNotificationsPage from './pages/MyNotificationsPage';
import StartApplicationPage from './pages/StartApplicationPage';

// Portals (role-gated)
import AgentPortalPage from './pages/AgentPortalPage';
import AgentLayout from './components/layout/AgentLayout';
import AgentDashboardPage from './pages/AgentDashboardPage';
import AgentMyApplicationsPage from './pages/AgentMyApplicationsPage';
import StudentPortalPage from './pages/StudentPortalPage';
import StudentDocumentUploadPage from './pages/StudentDocumentUploadPage';
import PublicFormPage from './pages/PublicFormPage';
import {
  PublicPortalPage,
  PortalSearchPage,
  PortalProgramDetailPage,
  PortalUniversitiesPage,
  PortalAboutPage,
  PortalContactPage,
  PortalStudentApplicationsPage,
  PortalLoginPage,
} from './pages/PublicPortalPage';

// AI Counselor
import AIProfilerChat from './components/AIProfilerChat';
import ErrorBoundary from './components/ErrorBoundary';

// Layout
import DashboardLayout from './components/layout/DashboardLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    toast.success('Logged out successfully');
  }, []);

  const api = useCallback(() => {
    const instance = axios.create({
      baseURL: API,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      timeout: 120000,
    });

    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          logout();
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, [token, logout]);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await api().get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [token, api]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      toast.success('Login successful!');
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (data) => {
    try {
      const response = await axios.post(`${API}/tenants/register`, data);
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      toast.success('Registration successful! Welcome to EduBridge Network');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, token, login, register, logout, api }}>
      {children}
    </AuthContext.Provider>
  );
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    // Honor an explicit returnTo target first (?next=… or location.state.from).
    const next = new URLSearchParams(location.search).get('next');
    const stateNext = location.state?.from?.pathname || location.state?.returnTo;
    const explicit = next || stateNext;
    if (explicit && !explicit.startsWith('/dashboard') && !explicit.startsWith('/agent')) {
      return <Navigate to={explicit} replace />;
    }
    // Otherwise route by role.
    if (user.user_type === 'student') return <Navigate to="/" replace />;
    if (user.user_type === 'agent') return <Navigate to="/agent/home" replace />;
    // Counsellor / desk-handler: a non-admin staff user (no super_admin flag,
    // not a vendor_admin / super_admin user_type) lands on the Applications
    // page pre-filtered to their own cases — NOT the admin dashboard. The
    // detection covers the "team_id is set" pattern (counsellor seeded into
    // a team), explicit user_type='counselor', and current_desk='counselor'.
    const isAdminish = user.is_super_admin === true
      || ['super_admin', 'vendor_admin', 'admin'].includes(user.user_type);
    const looksLikeCounsellor = !isAdminish && (
      user.user_type === 'counselor'
      || user.user_type === 'counsellor'
      || user.current_desk === 'counselor'
      || !!user.team_id
    );
    if (looksLikeCounsellor) return <Navigate to="/applications" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* PUBLIC WEBSITE */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/universities" element={<UniversitiesListPage />} />
            <Route path="/courses" element={<CoursesListPage />} />
            <Route path="/courses/:slug" element={<CourseDetailPage />} />
            <Route path="/countries" element={<CountriesListPage />} />
            <Route path="/scholarships" element={<ScholarshipsPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/terms-condition" element={<TermsPage />} />

            {/* AUTH */}
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />

            {/* STUDENT MY ACCOUNT (public-site, requires login) */}
            <Route path="/apply/:programId" element={<ProtectedRoute><StartApplicationPage /></ProtectedRoute>} />
            <Route path="/my-account" element={<ProtectedRoute><MyAccountPage /></ProtectedRoute>}>
              <Route path="profile" element={<MyProfilePage />} />
              <Route path="applications" element={<MyApplicationsPage />} />
              <Route path="notifications" element={<MyNotificationsPage />} />
            </Route>

            {/* PUBLIC FORMS / DOC UPLOAD (no auth) */}
            <Route path="/public/forms/:slug" element={<PublicFormPage />} />
            <Route path="/student-documents/:token" element={<StudentDocumentUploadPage />} />
            <Route path="/student/documents/:token" element={<StudentDocumentUploadPage />} />

            {/* PRODUCT MANUAL */}
            <Route path="/product-manual" element={<ProductManualPage />} />

            {/* PUBLIC PORTAL ROUTES (no auth) — workspace-scoped, kept for compatibility */}
            <Route path="/portal/:workspace/search" element={<PortalSearchPage />} />
            <Route path="/portal/:workspace/program/:programId" element={<PortalProgramDetailPage />} />
            <Route path="/portal/:workspace/universities" element={<PortalUniversitiesPage />} />
            <Route path="/portal/:workspace/about" element={<PortalAboutPage />} />
            <Route path="/portal/:workspace/contact" element={<PortalContactPage />} />
            <Route path="/portal/:workspace/my-applications" element={<PortalStudentApplicationsPage />} />
            <Route path="/portal/:workspace/login" element={<PortalLoginPage />} />
            <Route path="/portal/:workspace/:slug" element={<PublicPortalPage />} />

            {/* STUDENT / AGENT PORTALS (role-gated inside) */}
            <Route path="/student" element={<StudentPortalPage />} />
            <Route path="/study-portal" element={<StudentPortalPage />} />
            <Route path="/study-portal/search" element={<StudentPortalPage initialView="search" />} />
            <Route path="/study-portal/university/:universityId" element={<StudentPortalPage />} />
            <Route path="/study-portal/program/:programId" element={<StudentPortalPage />} />
            <Route path="/agent" element={<AgentPortalPage />} />
            <Route path="/agent-portal" element={<AgentPortalPage />} />

            {/* NEW minimalistic agent dashboard (uses regular JWT) */}
            <Route path="/agent/home" element={<ProtectedRoute><AgentLayout><AgentDashboardPage /></AgentLayout></ProtectedRoute>} />
            <Route path="/agent/applications" element={<ProtectedRoute><AgentLayout><AgentMyApplicationsPage /></AgentLayout></ProtectedRoute>} />

            {/* ADMIN (auth required) */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout><DashboardPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/applications" element={<ProtectedRoute><DashboardLayout><ApplicationsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/applications/:id" element={<ProtectedRoute><DashboardLayout><ErrorBoundary><ApplicationDetailPage /></ErrorBoundary></DashboardLayout></ProtectedRoute>} />
            {/* Agent-scoped application detail (uses minimal AgentLayout) */}
            <Route path="/agent/applications/:id" element={<ProtectedRoute><AgentLayout><ErrorBoundary><ApplicationDetailPage /></ErrorBoundary></AgentLayout></ProtectedRoute>} />
            <Route path="/archived" element={<ProtectedRoute><DashboardLayout><ArchivedPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/leads" element={<ProtectedRoute><DashboardLayout><LeadsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/ai-counselor" element={<ProtectedRoute><DashboardLayout hideMainPadding={true}><AIProfilerChat /></DashboardLayout></ProtectedRoute>} />
            <Route path="/forms" element={<ProtectedRoute><DashboardLayout><WebFormsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/web-forms" element={<ProtectedRoute><DashboardLayout><WebFormsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/forms/builder" element={<ProtectedRoute><DashboardLayout><FormCreatorPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/form-creator" element={<ProtectedRoute><DashboardLayout><FormCreatorPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/core-settings" element={<ProtectedRoute><DashboardLayout><CoreSettingsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><DashboardLayout><CoreSettingsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/org-settings" element={<ProtectedRoute><DashboardLayout><OrganizationSettingsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/organization-settings" element={<ProtectedRoute><DashboardLayout><OrganizationSettingsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/offices" element={<ProtectedRoute><DashboardLayout><OfficesPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/team" element={<ProtectedRoute><DashboardLayout><TeamPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/agents" element={<ProtectedRoute><DashboardLayout><AgentsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/agents/pending" element={<ProtectedRoute><DashboardLayout><PendingAgentsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/students" element={<ProtectedRoute><DashboardLayout><StudentsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/ai-credits" element={<ProtectedRoute><DashboardLayout><AICreditsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/import/courses" element={<ProtectedRoute><DashboardLayout><ImportCoursesPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/import-courses" element={<ProtectedRoute><DashboardLayout><ImportCoursesPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/ai-agents" element={<ProtectedRoute><DashboardLayout><AgentsManagerPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><DashboardLayout><NotificationsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/integrations/smtp" element={<ProtectedRoute><DashboardLayout><SMTPSettingsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/smtp-settings" element={<ProtectedRoute><DashboardLayout><SMTPSettingsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/integrations/twilio" element={<ProtectedRoute><DashboardLayout><TwilioSettingsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/twilio-settings" element={<ProtectedRoute><DashboardLayout><TwilioSettingsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/integrations" element={<ProtectedRoute><DashboardLayout><IntegrationsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/billing" element={<ProtectedRoute><DashboardLayout><BillingCreditsPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><DashboardLayout><ReportBuilderPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/report-builder" element={<ProtectedRoute><DashboardLayout><ReportBuilderPage /></DashboardLayout></ProtectedRoute>} />
            <Route path="/support" element={<ProtectedRoute><DashboardLayout><SupportPage /></DashboardLayout></ProtectedRoute>} />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
